import Tile from 'components/common/recommendation-tiles/tile';
import { isCompositionPickReachable } from 'utils/system-composition';
import {
  ComponentPickerOption,
  CompositionIndex,
  CompositionPickAction,
  CompositionPicks
} from 'utils/system-composition-types';

type WantPickerSectionProps<T extends string> = {
  heading: string;
  options: ComponentPickerOption<T>[];
  selectedId: T | null;
  picks: CompositionPicks;
  index: CompositionIndex;
  actionFor: (id: T) => CompositionPickAction;
  onPick: (action: CompositionPickAction) => void;
};

// One slot section of the want component picker (ported from the
// system-selector-frontend "System you want" stage): a titled row of
// single-select tiles where options that can't reach any real catalog
// combination render disabled, still visible, with the shared tooltip.
export default function WantPickerSection<T extends string>({
  heading,
  options,
  selectedId,
  picks,
  index,
  actionFor,
  onPick
}: WantPickerSectionProps<T>) {
  if (options.length === 0) {
    return null;
  }
  const entries = options.map((option) => ({
    option,
    selected: selectedId === option.id,
    reachable: isCompositionPickReachable(picks, actionFor(option.id), index)
  }));

  return (
    <div>
      <span className='basis-full text-[10.5px] font-bold uppercase tracking-widest text-gray-400'>
        {heading}
      </span>
      <div
        role='group'
        aria-label={heading}
        className='mt-1 flex flex-wrap items-center gap-2'
      >
        {entries.map(({ option, selected, reachable }) => (
          <Tile
            key={option.id}
            label={option.label}
            selected={selected}
            disabled={!selected && !reachable}
            tooltip={
              !selected && !reachable
                ? 'Not available with your other selections'
                : undefined
            }
            onClick={() => onPick(actionFor(option.id))}
          />
        ))}
      </div>
    </div>
  );
}
